import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  ErrorOutline as CritIcon,
  InfoOutlined as InfoIcon,
  Send as SendIcon,
  WarningAmber as WarnIcon,
} from '@mui/icons-material';
import { employeesApi, aiDocApi, DocAnomalyFlag } from '@/api/endpoints/hr';
import { TYPE } from '@/theme/typography';

const SEV_ICON: Record<string, React.ReactNode> = {
  CRITICAL: <CritIcon color="error" fontSize="small" />,
  WARNING: <WarnIcon color="warning" fontSize="small" />,
  INFO: <InfoIcon color="info" fontSize="small" />,
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

function AnomalySummary({ employeeId }: { employeeId: string }) {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['doc-intelligence-summary', employeeId],
    queryFn: () => aiDocApi.getEmployeeDocSummary(employeeId).then((r) => r.data),
    enabled: !!employeeId,
    staleTime: 60_000,
  });

  const refreshMutation = useMutation({
    mutationFn: () => aiDocApi.getEmployeeDocSummary(employeeId).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['doc-intelligence-summary', employeeId] }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', gap: 1, color: 'text.secondary', mb: 1 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontSize: TYPE.xs }}>Analysing documents…</Typography>
      </Box>
    );
  }

  if (isError || !data) return null;

  const { anomalies, critical_count, warning_count, total_docs } = data;

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: TYPE.xs, fontWeight: 600, color: 'text.secondary' }}>
          AI ANOMALY SCAN — {total_docs} doc{total_docs !== 1 ? 's' : ''}
        </Typography>
        {critical_count > 0 && (
          <Chip label={`${critical_count} critical`} color="error" size="small" />
        )}
        {warning_count > 0 && (
          <Chip label={`${warning_count} warnings`} color="warning" size="small" variant="outlined" />
        )}
        <Button
          size="small"
          variant="text"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          sx={{ ml: 'auto', fontSize: TYPE.xs }}
        >
          {refreshMutation.isPending ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>

      {anomalies.length === 0 ? (
        <Alert severity="success" sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: TYPE.sm }}>No anomalies detected across {total_docs} document(s).</Typography>
        </Alert>
      ) : (
        <List dense disablePadding>
          {anomalies.map((a: DocAnomalyFlag, i: number) => (
            <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>{SEV_ICON[a.severity]}</ListItemIcon>
              <ListItemText
                primary={
                  <Typography sx={{ fontSize: TYPE.sm }}>
                    <strong>{a.category}</strong> — {a.message}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}

function QAChat({ employeeId }: { employeeId: string }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const askUrl = aiDocApi.getDocIntelligenceAskUrl(employeeId);

  const send = async () => {
    const question = input.trim();
    if (!question || streaming) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setStreaming(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const apiBase = import.meta.env.VITE_API_URL || '';
    setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);

    try {
      const res = await fetch(`${apiBase}/api/v1${askUrl}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ question }),
        signal: ctrl.signal,
      });

      if (!res.body) throw new Error('No response body');
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));
            if (event.type === 'text_delta' || event.type === 'content_block_delta') {
              const chunk = event.delta?.text ?? event.text ?? '';
              setMessages((prev) => {
                const copy = [...prev];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: copy[copy.length - 1].content + chunk,
                };
                return copy;
              });
            }
          } catch {
            // skip malformed lines
          }
        }
      }
    } catch (err: unknown) {
      if ((err as Error).name !== 'AbortError') {
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: 'Error — could not complete the request.' };
          return copy;
        });
      }
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontSize: TYPE.xs, fontWeight: 600, color: 'text.secondary', mb: 1 }}>
        ASK ABOUT THIS EMPLOYEE'S DOCUMENTS
      </Typography>

      <Box
        sx={{
          minHeight: 180,
          maxHeight: 320,
          overflowY: 'auto',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          mb: 1,
          bgcolor: 'background.paper',
        }}
      >
        {messages.length === 0 && (
          <Typography sx={{ fontSize: TYPE.sm, color: 'text.disabled' }}>
            Ask anything about this employee's documents — e.g. "When does their work permit expire?" or "Do they have a signed NDA on file?"
          </Typography>
        )}
        {messages.map((m, i) => (
          <Box key={i} sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: TYPE.xs,
                fontWeight: 600,
                color: m.role === 'user' ? 'primary.main' : 'text.secondary',
                mb: 0.25,
              }}
            >
              {m.role === 'user' ? 'You' : 'AI'}
            </Typography>
            <Typography sx={{ fontSize: TYPE.sm, whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
          </Box>
        ))}
        {streaming && messages[messages.length - 1]?.content === '' && (
          <Box sx={{ display: 'flex', gap: 0.5, color: 'text.secondary' }}>
            <CircularProgress size={10} />
            <Typography sx={{ fontSize: TYPE.xs }}>Thinking…</Typography>
          </Box>
        )}
      </Box>

      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          fullWidth
          placeholder="Ask about documents…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          disabled={streaming}
        />
        <Button
          variant="contained"
          size="small"
          endIcon={streaming ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
          onClick={send}
          disabled={!input.trim() || streaming}
          sx={{ minWidth: 80 }}
        >
          {streaming ? 'Stop' : 'Ask'}
        </Button>
      </Stack>
    </Box>
  );
}

export default function DocIntelligencePanel() {
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; label: string } | null>(null);

  const { data: employees = [], isLoading: loadingEmps } = useQuery({
    queryKey: ['employees-list-for-doc-intel'],
    queryFn: () =>
      employeesApi.list({ page: 1, per_page: 200 }).then((r) =>
        r.data.items.map((e: { id: string; first_name: string; last_name: string; employee_number?: string }) => ({
          id: e.id,
          label: `${e.first_name} ${e.last_name}${e.employee_number ? ` (${e.employee_number})` : ''}`,
        }))
      ),
    staleTime: 120_000,
  });

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <AIIcon color="primary" />
        <Typography variant="h6" fontWeight={TYPE.bold}>
          Doc Intelligence
        </Typography>
      </Stack>

      <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', mb: 2 }}>
        Select an employee to run AI anomaly detection on their documents and ask questions about their document history.
      </Typography>

      <Autocomplete
        options={employees}
        loading={loadingEmps}
        value={selectedEmployee}
        onChange={(_, v) => setSelectedEmployee(v)}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => (
          <TextField {...params} label="Select employee" size="small" sx={{ mb: 2, maxWidth: 400 }} />
        )}
      />

      {selectedEmployee && (
        <Box>
          <AnomalySummary employeeId={selectedEmployee.id} />
          <Divider sx={{ my: 2 }} />
          <QAChat employeeId={selectedEmployee.id} />
        </Box>
      )}
    </Box>
  );
}
